import type { AxiosError } from "axios";
import { ApiError, type FieldErrors } from "../../errors";

/** Keys that carry the machine-readable code, never a field error. */
const CODE_KEYS = ["detail", "code"] as const;

/**
 * Pull the machine-readable code and/or DRF field errors out of a response body.
 * See {@link ApiError} for the shapes the backend actually emits.
 *
 * Two modules, two spellings. Identity puts the code in `detail`; ACQUIRE puts it in `code`
 * (verified in `modules/acquire/api/views.py` — `error_response()` builds `{"code": …}`). Both
 * are read here, because a client that only knew `detail` would see `code: null` on every
 * ACQUIRE domain error and be unable to tell `stale_config_revision` from
 * `round_configuration_frozen` — they are all 409s.
 *
 * Code and field errors are NOT mutually exclusive: a cross-field failure arrives as
 * `{"code": "invalid_general_configuration", "closing_at": ["…"]}`, and the UI needs both the
 * code (to react) and the field key (to attach the message). Earlier this returned on the
 * first hit and dropped whichever came second.
 */
function extractBody(body: unknown): {
  code: string | null;
  fieldErrors: FieldErrors | null;
} {
  if (!body || typeof body !== "object") {
    return { code: null, fieldErrors: null };
  }
  const record = body as Record<string, unknown>;

  let code: string | null = null;
  for (const key of CODE_KEYS) {
    const value = record[key];
    if (typeof value === "string") {
      code = value;
      break;
    }
  }

  // DRF field-error object: { field: string[], non_field_errors: string[], ... }
  const fieldErrors: FieldErrors = {};
  let hasFieldErrors = false;
  for (const [key, value] of Object.entries(record)) {
    if (CODE_KEYS.includes(key as (typeof CODE_KEYS)[number])) continue;
    if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
      fieldErrors[key] = value as string[];
      hasFieldErrors = true;
    }
  }

  return { code, fieldErrors: hasFieldErrors ? fieldErrors : null };
}

/** Normalize any axios error into the shared {@link ApiError}. status 0 == no response. */
export function toApiError(error: AxiosError): ApiError {
  const status = error.response?.status ?? 0;
  const body = error.response?.data ?? null;
  const { code, fieldErrors } = extractBody(body);
  return new ApiError({
    status,
    code,
    fieldErrors,
    body,
    message: error.message,
  });
}
